import { useState, useEffect, type ReactNode } from "react";
import { type ProductsQueryDto } from "../../../../api";
import { Button } from "../../../../components/common/button";
import { CheckOption } from "../../../../components/common/check-option";
import { Input } from "../../../../components/common/input";
import { Loader } from "../../../../components/common/loader";
import { Text } from "../../../../components/common/typography";
import { useBrands } from "../../hooks/useBrands";
import { useCategories } from "../../hooks/useCategories";

export type ProductsFilterContentProps = {
  filters: ProductsQueryDto;
  onFiltersChange: (filters: ProductsQueryDto) => void;
};

function FilterSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-t border-borderDefault py-5">
      <Text variant="capsSm" tone="accent" inline>
        {label}
      </Text>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

function toIdArray(value: unknown): number[] {
  if (value == null) return [];
  return Array.isArray(value) ? (value as number[]) : [value as number];
}

function toggleId(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

const WARRANTY_STANDARD_MONTHS = 36;

export function ProductsFilterContent({
  filters,
  onFiltersChange,
}: ProductsFilterContentProps) {
  const [local, setLocal] = useState<ProductsQueryDto>(filters);
  const { data: brands, isLoading: isLoadingBrands } = useBrands();
  const { data: categories, isLoading: isLoadingCategories } = useCategories();

  useEffect(() => setLocal(filters), [filters]);

  const categoryIds = toIdArray(local.categoryIds);
  const brandIds = toIdArray(local.brandIds);

  const toggleCategory = (id: number) =>
    setLocal((prev) => {
      const next = toggleId(categoryIds, id);
      return { ...prev, categoryIds: next.length ? next : undefined };
    });

  const toggleBrand = (id: number) =>
    setLocal((prev) => {
      const next = toggleId(brandIds, id);
      return { ...prev, brandIds: next.length ? next : undefined };
    });

  const warrantyStandard = local.minWarrantyMonths === WARRANTY_STANDARD_MONTHS;
  const warrantyExtended =
    (local.minWarrantyMonths ?? 0) > WARRANTY_STANDARD_MONTHS;

  const toggleStandardWarranty = (checked: boolean) =>
    setLocal((prev) => ({
      ...prev,
      minWarrantyMonths: checked ? WARRANTY_STANDARD_MONTHS : undefined,
    }));

  const toggleExtendedWarranty = (checked: boolean) =>
    setLocal((prev) => ({
      ...prev,
      minWarrantyMonths: checked ? WARRANTY_STANDARD_MONTHS + 1 : undefined,
    }));

  const inStockOnly = (local.minQty ?? 0) > 0;
  const toggleInStock = (checked: boolean) =>
    setLocal((prev) => ({ ...prev, minQty: checked ? 1 : undefined }));

  const [includePreorder, setIncludePreorder] = useState(false);

  const setMinPrice = (value: string) =>
    setLocal((prev) => ({
      ...prev,
      minPrice: value === "" ? undefined : Number(value),
    }));

  const setMaxPrice = (value: string) =>
    setLocal((prev) => ({
      ...prev,
      maxPrice: value === "" ? undefined : Number(value),
    }));

  const handleApply = () => onFiltersChange(local);
  const handleClear = () => {
    setLocal({});
    setIncludePreorder(false);
    onFiltersChange({});
  };

  const activeCount = categoryIds.length + brandIds.length
    + (inStockOnly ? 1 : 0) + (warrantyStandard || warrantyExtended ? 1 : 0)
    + (local.minPrice != null ? 1 : 0) + (local.maxPrice != null ? 1 : 0);

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable filter sections */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6">
        <div className="flex flex-col">
          <Text variant="bodySm" tone="muted" className="pb-2">
            Refine your search
          </Text>

          <FilterSection label="CATEGORY">
            {isLoadingCategories ? (
              <Loader inline size="sm" text="Loading categories..." />
            ) : (
              (categories ?? []).map((cat) => (
                <CheckOption
                  key={cat.id}
                  label={cat.name ?? "—"}
                  checked={categoryIds.includes(cat.id)}
                  onChange={() => toggleCategory(cat.id)}
                />
              ))
            )}
          </FilterSection>

          <FilterSection label="BRAND">
            {isLoadingBrands ? (
              <Loader inline size="sm" text="Loading brands..." />
            ) : (
              (brands ?? []).map((brand) => (
                <CheckOption
                  key={brand.id}
                  label={brand.name ?? "—"}
                  checked={brandIds.includes(brand.id)}
                  onChange={() => toggleBrand(brand.id)}
                />
              ))
            )}
          </FilterSection>

          <FilterSection label="WARRANTY">
            <CheckOption
              label="Standard (36m)"
              checked={warrantyStandard}
              onChange={toggleStandardWarranty}
            />
            <CheckOption
              label="Extended available"
              checked={warrantyExtended}
              onChange={toggleExtendedWarranty}
            />
          </FilterSection>

          <FilterSection label="STOCK STATUS">
            <CheckOption
              label="In stock only"
              checked={inStockOnly}
              onChange={toggleInStock}
            />
            <CheckOption
              label="Include pre-order"
              checked={includePreorder}
              onChange={setIncludePreorder}
            />
          </FilterSection>

          <FilterSection label="PRICE RANGE (QAR)">
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                name="minPrice"
                label="Minimum"
                value={local.minPrice?.toString() ?? ""}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="0"
                min={0}
                className="mb-0"
              />
              <Input
                type="number"
                name="maxPrice"
                label="Maximum"
                value={local.maxPrice?.toString() ?? ""}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="10000"
                min={0}
                className="mb-0"
              />
            </div>
          </FilterSection>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="shrink-0 border-t border-borderDefault bg-bgWhite px-6 py-6">
        <div className="flex gap-3">
          <div className="flex-1">
            <Button variant="primary" fullWidth onClick={handleApply}>
              Apply filters{activeCount > 0 ? ` (${activeCount})` : ""}
            </Button>
          </div>
          <div className="flex-1">
            <Button variant="secondary" fullWidth onClick={handleClear}>
              Clear all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductsFilterContent;
